<template>
  <div class="afiliados-page">

    <!-- ─── Hero ─── -->
    <section class="afiliados-hero">
      <div class="container">
        <h1 class="hero-title">Productos en Mercado Libre</h1>

        <p class="ml-disclaimer-pill">
          <span class="ml-badge">ML</span>
          Al comprar por estos links nos apoyas sin costo extra para ti.
        </p>
      </div>
    </section>

    <!-- ─── Nav sections ─── -->
    <div class="section-nav">
      <div class="container">
        <a class="section-nav-btn active-nav" href="#productos" @click.prevent="scrollTo('productos')">
          <i class="fas fa-shopping-bag"></i> Productos
        </a>
        <a class="section-nav-btn" href="#cupones" @click.prevent="scrollTo('cupones')">
          <i class="fas fa-ticket"></i> Cupones
        </a>
        <a class="section-nav-btn" href="#ofertas-bancarias" @click.prevent="scrollTo('ofertas-bancarias')">
          <i class="fas fa-credit-card"></i> Ofertas Bancarias
        </a>
      </div>
    </div>

    <!-- ─── Productos ─── -->
    <section id="productos" class="productos-section">
      <div class="container">

        <!-- Cargando -->
        <div class="loading-state" v-if="loading">
          <div class="skeleton-grid">
            <div class="skeleton-card" v-for="n in 6" :key="n"></div>
          </div>
        </div>

        <!-- Error -->
        <div class="empty-state" v-else-if="error">
          <i class="fas fa-wifi-slash"></i>
          <p>{{ error }}</p>
        </div>

        <template v-else>

        <!-- Filtros + toggle vista -->
        <div class="filters-row">
          <div class="filters" v-if="categorias.length > 1">
            <button
              class="filter-btn"
              :class="{ active: categoriaActiva === '' }"
              @click="categoriaActiva = ''"
            >Todos</button>
            <button
              v-for="cat in categorias"
              :key="cat"
              class="filter-btn"
              :class="{ active: categoriaActiva === cat }"
              @click="categoriaActiva = cat"
            >{{ cat }}</button>
          </div>
          <div class="view-toggle">
            <button class="view-btn" :class="{ active: vista === 'grid' }" @click="vista = 'grid'" title="Vista cuadrícula">
              <i class="fas fa-th"></i>
            </button>
            <button class="view-btn" :class="{ active: vista === 'list' }" @click="vista = 'list'" title="Vista lista">
              <i class="fas fa-list"></i>
            </button>
            <button class="view-btn" :class="{ active: vista === 'table' }" @click="vista = 'table'" title="Vista tabla">
              <i class="fas fa-table"></i>
            </button>
          </div>
        </div>

        <!-- Sin productos -->
        <div class="empty-state" v-if="productosFiltrados.length === 0">
          <i class="fas fa-box-open"></i>
          <p>Próximamente agregaremos productos. ¡Vuelve pronto!</p>
        </div>

        <!-- Grid / List -->
        <div v-if="vista !== 'table'"
          :class="vista === 'grid' ? 'product-grid' : 'product-list'"
          v-show="productosFiltrados.length > 0"
        >
          <a
            v-for="producto in productosFiltrados"
            :key="producto.id"
            :href="producto.link || '#'"
            target="_blank"
            rel="noopener noreferrer sponsored"
            class="product-card"
            :class="{ 'product-card--list': vista === 'list' }"
            @click="producto.link && producto.link !== '#' ? null : $event.preventDefault()"
          >
            <div class="product-thumb">
              <img v-if="producto.imagen" :src="producto.imagen" :alt="producto.nombre" loading="lazy" />
              <i v-else class="fas fa-image product-thumb-icon"></i>
            </div>
            <div class="product-body">
              <span v-if="producto.badge" class="product-badge" :class="badgeClass(producto.badge)">
                {{ producto.badge }}
              </span>
              <h3>{{ producto.nombre }}</h3>
              <p v-if="producto.descripcion" class="product-desc">{{ producto.descripcion }}</p>
              <div class="product-price-row" v-if="producto.precio">
                <span v-if="producto.precio_antes" class="price-before">$ {{ producto.precio_antes }}</span>
                <span class="price-now">$ {{ producto.precio }}</span>
                <span v-if="producto.descuento" class="price-discount">-{{ producto.descuento }}%</span>
              </div>
              <div class="product-footer">
                <span class="btn-ml btn-sm">
                  <span class="ml-icon">ML</span> Visitar
                  <i class="fas fa-external-link-alt"></i>
                </span>
              </div>
            </div>
          </a>
        </div>

        <!-- Table -->
        <div v-else class="product-table-wrap">
          <table class="product-table">
            <thead>
              <tr>
                <th></th>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Precio</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="producto in productosFiltrados" :key="producto.id">
                <td>
                  <img v-if="producto.imagen" :src="producto.imagen" :alt="producto.nombre" class="table-thumb" loading="lazy" />
                  <i v-else class="fas fa-image table-thumb-icon"></i>
                </td>
                <td>
                  <div class="table-name">{{ producto.nombre }}</div>
                  <div v-if="producto.descripcion" class="table-desc">{{ producto.descripcion }}</div>
                </td>
                <td><span class="table-cat">{{ producto.categoria || '—' }}</span></td>
                <td>
                  <div class="table-price">
                    <span v-if="producto.precio_antes" class="price-before">$ {{ producto.precio_antes }}</span>
                    <span class="price-now">{{ producto.precio ? '$ ' + producto.precio : '—' }}</span>
                    <span v-if="producto.descuento" class="price-discount">-{{ producto.descuento }}%</span>
                  </div>
                </td>
                <td>
                  <a
                    :href="producto.link || '#'"
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    class="btn-ml btn-sm"
                    @click="producto.link && producto.link !== '#' ? null : $event.preventDefault()"
                  >
                    <span class="ml-icon">ML</span> Comprar
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        </template>

      </div>
    </section>

    <!-- ─── Cupones ─── -->
    <section id="cupones" class="cupones-section" v-if="cupones.length > 0">
      <div class="container">
        <span class="label-chip label-chip--light"><i class="fas fa-ticket"></i> Descuentos activos</span>
        <h2 class="section-title">Cupones Disponibles</h2>
        <p class="section-desc">Copia el código y úsalo al pagar en Mercado Libre para obtener tu descuento.</p>

        <div class="ml-disclaimer ml-disclaimer--sm">
          <span class="ml-badge">ML</span>
          Cupones válidos exclusivamente en <strong>Mercado Libre</strong>. Cópialos y pégalos al momento de pagar.
        </div>

        <div class="cupones-grid">
          <div v-for="cupon in cuponesActivos" :key="cupon.id" class="cupon-card">
            <div class="cupon-side">
              <i :class="cuponIcon(cupon.descuento)"></i>
              <div class="cupon-pct">
                {{ cupon.descuento }}
              </div>
            </div>
            <div class="cupon-body">
              <div class="cupon-platform">Cupón Mercado Libre</div>
              <h4>{{ cupon.titulo }}</h4>
              <p>{{ cupon.descripcion }}</p>
              <div class="cupon-code-row">
                <span class="cupon-code">{{ cupon.codigo }}</span>
                <button class="copy-btn" @click="copiarCodigo(cupon)" :title="'Copiar ' + cupon.codigo">
                  <i :class="cupon.copiado ? 'fas fa-check' : 'fas fa-copy'"></i>
                </button>
              </div>
              <div v-if="cupon.vigencia" class="cupon-validity" :class="{ 'expira-pronto': expiraProxima(cupon.vigencia) }">
                <i :class="expiraProxima(cupon.vigencia) ? 'fas fa-fire' : 'fas fa-clock'"></i>
                {{ expiraProxima(cupon.vigencia) ? '¡Expira pronto! ' : '' }}Válido hasta el {{ formatFecha(cupon.vigencia) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Ofertas Bancarias ─── -->
    <section id="ofertas-bancarias" class="bancarias-section" v-if="ofertasBancarias.length > 0">
      <div class="container">
        <span class="label-chip label-chip--light"><i class="fas fa-credit-card"></i> Promociones bancarias</span>
        <h2 class="section-title">Ofertas Bancarias</h2>
        <p class="section-desc">Meses sin intereses y promociones disponibles al pagar en Mercado Libre.</p>
        <div class="bancarias-grid">
          <div v-for="oferta in ofertasBancarias" :key="oferta.id" class="bancaria-card">
            <div class="bancaria-side">
              <i class="fas fa-university"></i>
            </div>
            <div class="bancaria-body">
              <div class="bancaria-banco">{{ oferta.banco }}</div>
              <h4>{{ oferta.titulo }}</h4>
              <p>{{ oferta.descripcion }}</p>
              <div v-if="oferta.vigencia" class="cupon-validity">
                <i class="fas fa-clock"></i> Válido hasta el {{ formatFecha(oferta.vigencia) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Aviso legal ML ─── -->
    <section class="legal-section">
      <div class="container">
        <p class="legal-note">
          <i class="fas fa-info-circle"></i>
          Neliel Papelería participa en el Programa de Afiliados de Mercado Libre. Los productos mostrados son vendidos y gestionados directamente por Mercado Libre o sus vendedores. Al hacer clic en un enlace y realizar una compra, Neliel puede recibir una comisión sin ningún costo adicional para ti.
        </p>
      </div>
    </section>

  </div>
</template>

<script>
import productosData from '../data/productos.json'
import cuponesData   from '../data/cupones.json'

// Endpoint único del catálogo en la API propia (SmarterASP.NET).
// Devuelve { productos: [], cupones: [], bancarias: [] } — ver
// documents/api-afiliados-endpoints.md para el contrato completo.
const API_CATALOGO = process.env.VUE_APP_API_CATALOGO

// ─── Configuración de caché ───────────────────────────────────────────────────
// Tiempo en minutos antes de volver a consultar la API.
// Cambia este valor según necesites (mínimo recomendado: 5).
const CACHE_TTL_MINUTOS = 60
// ─────────────────────────────────────────────────────────────────────────────

const CACHE_KEY = 'neliel_afiliados_v2' // v2: shape nuevo {productos, cupones, bancarias}
const CACHE_TTL = CACHE_TTL_MINUTOS * 60 * 1000

function leerCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { data, timestamp } = JSON.parse(raw)
    if (Date.now() - timestamp > CACHE_TTL) return null
    return data
  } catch {
    return null
  }
}

function guardarCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }))
  } catch {
    // localStorage lleno o bloqueado — continúa sin caché
  }
}

export default {
  name: 'AfiliadosML',
  data() {
    return {
      productos:         [],
      cupones:           [],
      ofertasBancarias:  [],
      categoriaActiva:   '',
      vista:             'grid',
      loading:           true,
      error:             null,
    }
  },
  async mounted() {
    // Sin API configurada (dev sin backend) — usar los JSON locales de respaldo
    if (!API_CATALOGO) {
      this.aplicarCatalogo({ productos: productosData, cupones: cuponesData })
      this.loading = false
      return
    }

    // Intentar leer del caché antes de llamar a la API
    const cached = leerCache()
    if (cached) {
      this.aplicarCatalogo(cached)
      this.loading = false
      return
    }

    // Caché vencido o vacío — un solo fetch al catálogo completo
    try {
      const res = await fetch(API_CATALOGO)
      if (!res.ok) throw new Error('Error en la respuesta')
      const catalogo = await res.json()
      guardarCache(catalogo)
      this.aplicarCatalogo(catalogo)
    } catch {
      this.error = 'No se pudieron cargar los productos. Intenta más tarde.'
    } finally {
      this.loading = false
    }
  },
  computed: {
    categorias() {
      return [...new Set(this.productos.map(p => p.categoria).filter(Boolean))]
    },
    productosFiltrados() {
      if (!this.categoriaActiva) return this.productos
      return this.productos.filter(p => p.categoria === this.categoriaActiva)
    },
    cuponesActivos() {
      return this.cupones.filter(c => !c.vigencia || new Date(c.vigencia) >= new Date())
    },
  },
  methods: {
    aplicarCatalogo(catalogo) {
      this.productos        = catalogo.productos || []
      this.cupones          = (catalogo.cupones || []).map(c => ({ ...c, copiado: false }))
      this.ofertasBancarias = catalogo.bancarias || []
    },
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    badgeClass(badge) {
      const map = { 'Oferta': 'badge-oferta', 'Nuevo': 'badge-nuevo', 'Recomendado': 'badge-rec' }
      return map[badge] ?? 'badge-rec'
    },
    cuponIcon(descuento) {
      if (!descuento) return 'fas fa-tag'
      const d = descuento.toUpperCase()
      if (d.includes('ENVÍO') || d.includes('ENVIO')) return 'fas fa-shipping-fast'
      if (d.includes('%')) return 'fas fa-percent'
      return 'fas fa-gift'
    },
    expiraProxima(vigencia) {
      const dias = (new Date(vigencia) - new Date()) / (1000 * 60 * 60 * 24)
      return dias <= 7
    },
    formatFecha(vigencia) {
      return new Date(vigencia).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
    },
    async copiarCodigo(cupon) {
      try {
        await navigator.clipboard.writeText(cupon.codigo)
        cupon.copiado = true
        setTimeout(() => { cupon.copiado = false }, 1800)
      } catch {
        // fallback silencioso
      }
    },
  },
}
</script>

<style scoped>
/* ── Variables ── */
.afiliados-page {
  --teal:  #003b4c;
  --coral: #ed8f7b;
  --gold:  #f6dfa4;
  --peach: #f0c2b2;
  --cream: #fef9ec;
  --muted: #6b8a94;
  --text:  #1a2e35;
  font-family: 'Inter', 'Poppins', sans-serif;
  color: var(--text);
}

.container { max-width: 1120px; margin: 0 auto; padding: 0 24px; }

/* ── Hero ── */
.afiliados-hero {
  background: var(--teal);
  padding: 72px 0 56px;
  position: relative;
  overflow: hidden;
  text-align: center;
}
.afiliados-hero .container {
  display: flex; flex-direction: column; align-items: center;
}
.afiliados-hero .ml-disclaimer {
  justify-content: center;
}
.afiliados-hero::after {
  content: '';
  position: absolute; bottom: -2px; left: 0; right: 0; height: 48px;
  background: #f7f4ef;
  clip-path: ellipse(55% 100% at 50% 100%);
}

.label-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.72rem; font-weight: 600; letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.14);
  padding: 5px 14px; border-radius: 999px; margin-bottom: 14px;
}
.label-chip i { color: var(--gold); }

.label-chip--light {
  color: var(--muted);
  background: rgba(0,59,76,0.07); border-color: rgba(0,59,76,0.12);
}
.label-chip--light i { color: var(--coral); }

.hero-title {
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700; color: #fff;
  margin-bottom: 12px; line-height: 1.2;
}
.hero-desc { font-size: 1rem; color: rgba(255,255,255,0.6); margin-bottom: 24px; }

/* ── Disclaimer ── */
.ml-disclaimer-pill {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
  border-radius: 999px; padding: 8px 20px;
  font-size: 0.8rem; color: rgba(255,255,255,0.7);
  margin-top: 4px;
}
.ml-disclaimer--sm {
  background: var(--cream); border-color: rgba(237,143,123,0.3);
  color: var(--text); margin-bottom: 32px;
}
.ml-badge {
  background: var(--coral); color: #fff;
  font-weight: 800; font-size: 0.65rem;
  padding: 2px 7px; border-radius: 5px; flex-shrink: 0;
}

/* ── Section Nav ── */
.section-nav {
  background: #fff;
  border-bottom: 1.5px solid rgba(0,59,76,0.08);
  padding: 0 0 2px;
  position: sticky; top: 0; z-index: 10;
}
.section-nav .container {
  display: flex; gap: 4px; padding-top: 12px; padding-bottom: 12px;
  flex-wrap: wrap;
}
.section-nav-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 999px;
  font-size: 0.8rem; font-weight: 600;
  color: var(--muted); text-decoration: none;
  border: 1.5px solid transparent;
  transition: all 0.15s;
}
.section-nav-btn:hover {
  background: rgba(0,59,76,0.06); color: var(--teal);
}
.section-nav-btn.active-nav {
  background: var(--teal); color: #fff;
  border-color: var(--teal);
}
.section-nav-btn i { font-size: 0.8rem; }

/* ── Productos ── */
.productos-section { background: #f7f4ef; padding: 56px 0 64px; }

.section-title {
  font-family: 'Lora', serif;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700; color: var(--teal);
  margin-bottom: 8px; line-height: 1.2;
}
.section-desc { font-size: 0.97rem; color: var(--muted); margin-bottom: 32px; }

/* Filtros + toggle */
.filters-row {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 12px; margin-bottom: 32px;
}
.filters { display: flex; gap: 10px; flex-wrap: wrap; }
.filter-btn {
  padding: 8px 18px; border-radius: 999px; font-size: 0.8rem; font-weight: 600;
  border: 1.5px solid rgba(0,59,76,0.18); background: #fff;
  color: var(--teal); cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.filter-btn:hover, .filter-btn.active {
  background: var(--teal); color: #fff; border-color: var(--teal);
}

/* View toggle */
.view-toggle { display: flex; gap: 6px; }
.view-btn {
  width: 36px; height: 36px; border-radius: 10px;
  border: 1.5px solid rgba(0,59,76,0.18); background: #fff;
  color: var(--muted); cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center; font-size: 0.85rem;
}
.view-btn:hover, .view-btn.active {
  background: var(--teal); color: #fff; border-color: var(--teal);
}

/* Description */
.product-desc {
  font-size: 0.78rem; color: var(--muted); line-height: 1.4;
  margin-bottom: 10px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

/* Price */
.product-price-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.price-before { font-size: 0.78rem; color: var(--muted); text-decoration: line-through; }
.price-now    { font-size: 1rem; font-weight: 700; color: var(--teal); }
.price-discount {
  font-size: 0.7rem; font-weight: 700; color: #fff;
  background: #2e9e5b; border-radius: 999px; padding: 2px 8px;
}

/* List view */
.product-list { display: flex; flex-direction: column; gap: 16px; }
.product-card--list { flex-direction: row !important; border-radius: 14px; }
.product-card--list .product-thumb { width: 120px; height: 120px; flex-shrink: 0; border-radius: 0; }
.product-card--list .product-body  { display: flex; align-items: center; gap: 16px; flex: 1; padding: 16px; }
.product-card--list h3             { flex: 1; margin-bottom: 0; }
.product-card--list .product-footer { border-top: none; padding-top: 0; }

/* Table view */
.product-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.product-table {
  width: 100%; min-width: 560px; border-collapse: collapse;
  background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,59,76,0.07);
}
.product-table th {
  background: var(--teal); color: #fff;
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; padding: 14px 16px; text-align: left;
}
.product-table td {
  padding: 14px 16px; border-bottom: 1px solid rgba(0,59,76,0.07);
  vertical-align: middle; font-size: 0.85rem;
}
.product-table tr:last-child td { border-bottom: none; }
.product-table tr:hover td { background: #f8fbff; }
.table-thumb { width: 60px; height: 60px; object-fit: cover; border-radius: 8px; display: block; }
.table-thumb-icon { font-size: 1.5rem; color: rgba(0,59,76,0.2); }
.table-name { font-weight: 600; color: var(--teal); margin-bottom: 4px; }
.table-desc { font-size: 0.75rem; color: var(--muted); }
.table-cat  { font-size: 0.75rem; color: var(--muted); }
.table-price { display: flex; flex-direction: column; gap: 2px; }

/* Loading skeleton */
.loading-state { padding: 8px 0 32px; }
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
}
.skeleton-card {
  height: 320px; border-radius: 18px;
  background: linear-gradient(90deg, #e8e4df 25%, #f0ede9 50%, #e8e4df 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Empty / error state */
.empty-state {
  text-align: center; padding: 64px 24px; color: var(--muted);
}
.empty-state i { font-size: 3rem; margin-bottom: 16px; display: block; opacity: 0.4; }
.empty-state p { font-size: 1rem; }

/* Grid de productos */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
}

.product-card {
  background: #fff;
  border: 1.5px solid rgba(0,59,76,0.09);
  border-radius: 18px; overflow: hidden;
  text-decoration: none; color: inherit;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex; flex-direction: column;
  position: relative;
}
.product-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
  background: var(--coral);
}
.product-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,59,76,0.1); }

.product-thumb {
  height: 180px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  background: #f7f4ef;
}
.product-thumb img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform 0.3s;
}
.product-card:hover .product-thumb img { transform: scale(1.05); }
.product-thumb-icon { font-size: 2.5rem; color: rgba(0,59,76,0.2); }

.product-body { padding: 16px; flex: 1; display: flex; flex-direction: column; }
.product-badge {
  display: inline-block; font-size: 0.65rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 3px 10px; border-radius: 999px; margin-bottom: 8px; align-self: flex-start;
}
.badge-oferta   { background: #fff0ed; color: var(--coral); }
.badge-nuevo    { background: #f0faf4; color: #2e9e5b; }
.badge-rec      { background: var(--cream); color: #8a6730; }

.product-body h3 {
  font-size: 0.92rem; font-weight: 600; color: var(--teal);
  line-height: 1.35; flex: 1; margin-bottom: 12px;
}

.product-footer {
  border-top: 1px solid rgba(0,59,76,0.07);
  padding-top: 12px; display: flex; flex-direction: column; gap: 8px;
}
.ext-note {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.7rem; color: var(--muted);
}
.dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--coral); flex-shrink: 0;
}
.btn-ml {
  display: inline-flex; align-items: center; gap: 7px;
  background: var(--coral); color: #fff;
  padding: 9px 18px; border-radius: 999px;
  font-size: 0.8rem; font-weight: 700;
  justify-content: center;
}
.ml-icon {
  background: rgba(255,255,255,0.25); border-radius: 4px;
  font-size: 0.6rem; font-weight: 900; padding: 2px 5px;
}

/* ── Cupones ── */
.cupones-section { background: var(--cream); padding: 72px 0; }

.cupones-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.cupon-card {
  background: #fff; border: 1.5px solid rgba(0,59,76,0.09);
  border-radius: 18px; overflow: hidden;
  display: flex; position: relative;
}
.cupon-card::before {
  content: ''; position: absolute; top: 0; left: 0; bottom: 0;
  width: 5px; background: var(--coral); border-radius: 18px 0 0 18px;
}

.cupon-side {
  width: 84px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--teal), #005670);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 20px 8px; gap: 8px;
}
.cupon-side i { font-size: 1.4rem; color: var(--gold); }
.cupon-pct {
  font-size: 1rem; font-weight: 800; color: #fff;
  text-align: center; line-height: 1.2; word-break: break-word;
}

.cupon-body { padding: 16px 16px 16px 20px; flex: 1; }
.cupon-platform {
  font-size: 0.68rem; color: var(--coral); font-weight: 700;
  margin-bottom: 4px; display: flex; align-items: center; gap: 5px;
}
.cupon-platform::before {
  content: ''; width: 6px; height: 6px; border-radius: 50%;
  background: var(--coral); display: inline-block;
}
.cupon-body h4 { font-size: 0.92rem; font-weight: 700; color: var(--teal); margin-bottom: 4px; }
.cupon-body p  { font-size: 0.79rem; color: var(--muted); margin-bottom: 12px; line-height: 1.4; }

.cupon-code-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.cupon-code {
  font-family: monospace; font-size: 0.88rem; font-weight: 700;
  background: rgba(0,59,76,0.06); color: var(--teal);
  padding: 5px 12px; border-radius: 8px;
  border: 1.5px dashed rgba(0,59,76,0.2); letter-spacing: 0.1em;
}
.copy-btn {
  width: 30px; height: 30px; border-radius: 8px;
  background: var(--teal); color: #fff; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  font-size: 0.75rem; transition: background 0.15s; flex-shrink: 0;
}
.copy-btn:hover { background: #005670; }

.cupon-validity { font-size: 0.72rem; color: var(--muted); display: flex; align-items: center; gap: 5px; }
.cupon-validity.expira-pronto { color: #c0392b; font-weight: 600; }

/* ── Ofertas Bancarias ── */
.bancarias-section { background: #f0f7f4; padding: 72px 0; }
.bancarias-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}
.bancaria-card {
  background: #fff; border: 1.5px solid rgba(0,59,76,0.09);
  border-radius: 18px; overflow: hidden;
  display: flex; position: relative;
}
.bancaria-card::before {
  content: ''; position: absolute; top: 0; left: 0; bottom: 0;
  width: 5px; background: var(--teal); border-radius: 18px 0 0 18px;
}
.bancaria-side {
  width: 64px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--teal), #005670);
  display: flex; align-items: center; justify-content: center;
}
.bancaria-side i { font-size: 1.4rem; color: var(--gold); }
.bancaria-body { padding: 16px 16px 16px 20px; flex: 1; }
.bancaria-banco {
  font-size: 0.68rem; color: var(--teal); font-weight: 700;
  margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.08em;
}
.bancaria-body h4 { font-size: 0.92rem; font-weight: 700; color: var(--teal); margin-bottom: 4px; }
.bancaria-body p  { font-size: 0.79rem; color: var(--muted); line-height: 1.4; }

/* ── Aviso legal ── */
.legal-section { background: #f7f4ef; padding: 32px 0; }
.legal-note {
  font-size: 0.78rem; color: var(--muted); line-height: 1.6;
  max-width: 800px; margin: 0 auto; text-align: center;
  display: flex; align-items: flex-start; gap: 8px;
}
.legal-note i { color: var(--coral); margin-top: 2px; flex-shrink: 0; }

@media (max-width: 640px) {
  .product-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 16px; }
  .product-thumb { height: 140px; }
  .cupon-side { width: 70px; }
  .cupones-grid { grid-template-columns: 1fr; }
  .bancarias-grid { grid-template-columns: 1fr; }
  .afiliados-hero { padding: 48px 0 40px; }
  .container { padding: 0 16px; }

  /* Nav de secciones: fila scrolleable en vez de envolver */
  .section-nav .container { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
  .section-nav .container::-webkit-scrollbar { display: none; }
  .section-nav-btn { flex-shrink: 0; white-space: nowrap; padding: 8px 14px; font-size: 0.78rem; }

  /* Filtros scrolleables, toggle de vista debajo */
  .filters-row { flex-direction: column; align-items: stretch; gap: 10px; }
  .filters { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding-bottom: 4px; }
  .filters::-webkit-scrollbar { display: none; }
  .filter-btn { flex-shrink: 0; white-space: nowrap; }
  .view-toggle { justify-content: flex-end; }
  .view-btn { width: 40px; height: 40px; }

  /* Vista lista: contenido apilado */
  .product-card--list .product-thumb { width: 100px; height: 100px; }
  .product-card--list .product-body { flex-direction: column; align-items: flex-start; gap: 6px; padding: 12px; }

  /* Cupones y ofertas más compactos */
  .cupon-body, .bancaria-body { padding: 14px; }
  .cupon-code-row { flex-wrap: wrap; }
  .copy-btn { width: 38px; height: 38px; }

  .ml-disclaimer-pill { padding: 8px 14px; font-size: 0.74rem; text-align: left; }
  .legal-note { flex-direction: column; align-items: center; gap: 6px; }

  .productos-section { padding: 40px 0 48px; }
  .cupones-section, .bancarias-section { padding: 48px 0; }
}
</style>
